import { Box, Button, Paper, Skeleton, Stack } from "@mui/material";
import { CloseCircle20Px, useToast } from "@qubit/autoparts";

import { skipToken } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBinWithAnimation } from "~/features/autostoreBin/AutostoreBinWithAnimation";
import { usePortStatusQuery } from "~/hooks/usePortStatus";

import { getBinCompartmentOccupancy } from "~/lib/binCompartmentOccupancy";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectIsShortSideBin,
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import {
  useConfirmInventoryMovementMutation,
  usePostSkipMaintenanceInventoryMovementMutation,
  useRequestNextMaintenanceBinMutation
} from "~/redux/warehouse/autostoreBinMaintenance.hooks";
import {
  useCloseBinMutation,
  useClosePortMutation,
  useDeleteBinFlagMutation
} from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useDeleteInventoryHoldMutation,
  useGetInventoryByAutostoreBinNumberQuery
} from "~/redux/warehouse/inventory.hooks";

import { BinNotEmptyButton } from "./BinNotEmptyButton";
import { FlagBinButton } from "./FlagBinButton";
import { MoveInventoryButton } from "./MoveInventoryButton";
import { PulsingArrows } from "./PulsingArrows";
import { UnflagBinButton } from "./UnflagBinButton";
import {
  setSourceDestinationBins,
  setTaskState,
  setSelectedPort,
  setIsDestinationBinReady,
  setIsSourceBinReady
} from "./binMaintenanceWorkstation.slice";

export const BinMaintenancePort = ({
  portId,
  isLoading
}: {
  portId: number;
  isLoading: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { errorToast, warningToast, successToast } = useToast();
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  const [requestNextBin] = useRequestNextMaintenanceBinMutation();
  const [deleteBinFlag] = useDeleteBinFlagMutation();
  const [skipInventoryMovement] =
    usePostSkipMaintenanceInventoryMovementMutation();
  const [removeHold] = useDeleteInventoryHoldMutation();

  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const {
    movementTaskState,
    movementData,
    selectedPort,
    isDestinationBinReady: isDestinationBinReadyState,
    isSourceBinReady: isSourceBinReadyState
  } = useAppSelector((state) => state.binMaintenanceWorkstation);

  const { error, activityState, binState, refetch, ...portStatus } =
    usePortStatusQuery(portId);

  const [
    confirmInventoryMovement,
    { isLoading: confirmInventoryMovementIsLoading }
  ] = useConfirmInventoryMovementMutation();
  const [closeBin] = useCloseBinMutation();
  const [closePort] = useClosePortMutation();

  const cleaningBin = movementTaskState === "CleaningBin";
  const movingProduct = movementTaskState === "MovingProduct";
  const confirmingMovement = movementTaskState === "ConfirmingMovement";
  const { selectedBin: thisBinNumber, isReady, selectedTask } = portStatus;
  const { sourceBin, destinationBin } =
    movementData?.inventoryMovements?.sourceAndDestinationBin || {};
  const binCleaningPorts = movementData?.binCleaningPorts;
  const isCleaningBin = movementData?.binCleaningPorts?.includes(portId);
  const isSelectedCleaningBin = isCleaningBin && portId === selectedPort;
  const isSourceBin = !!(
    sourceBin &&
    thisBinNumber &&
    thisBinNumber === sourceBin.autostoreBinNumber
  );
  const isDestinationBin = !!(
    destinationBin &&
    thisBinNumber &&
    thisBinNumber === destinationBin.autostoreBinNumber
  );
  const isDestinationBinReady = isDestinationBin && isReady;
  const isSourceBinReady = isSourceBin && isReady;

  const {
    data: binInventory,
    refetch: refetchBinInventory,
    isFetching: binInventoryIsFetching
  } = useGetInventoryByAutostoreBinNumberQuery(
    autostoreGridId && (isSourceBin || (isDestinationBin && confirmingMovement))
      ? {
          autostoreGridId,
          binNumber: thisBinNumber
        }
      : skipToken
  );

  const sourceCompartmentNumbersWithQty =
    binInventory &&
    ((isSourceBin && movingProduct) || (isDestinationBin && confirmingMovement))
      ? binInventory.map((inventory) => ({
          autostoreCompartmentNumber: inventory.bin.autostoreCompartmentNumber,
          quantity: inventory.count.value
        }))
      : undefined;

  const moveButtonDisabled = !movingProduct || !isReady;

  const makeNextBinRequest = useCallback(
    async (requestingPortId: number) => {
      if (!autostoreGridId) return;
      try {
        const nextBinData = await requestNextBin({
          autostoreGridId,
          portId: requestingPortId
        }).unwrap();
        // there are no more empty bins to unflag
        if (nextBinData === null) {
          await closePort({
            autostoreGridId,
            portId: requestingPortId
          });
          successToast(
            t("bin cleaning tasks for port portid completed", {
              portid: String(requestingPortId)
            })
          );
          dispatch(setTaskState(null));
        }
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
      }
    },
    [
      autostoreGridId,
      requestNextBin,
      errorToast,
      closePort,
      dispatch,
      successToast,
      t
    ]
  );

  const getNextInventoryMovement = useCallback(async () => {
    if (
      !autostoreGridId ||
      !sourceBin ||
      !destinationBin ||
      !selectedWorkstation
    )
      return;
    try {
      const nextInventoryMovement = await confirmInventoryMovement({
        autostoreGridId,
        workstationId: selectedWorkstation.id,
        sourceBinId: sourceBin.autostoreBinNumber,
        destinationBinId: destinationBin.autostoreBinNumber
      }).unwrap();
      dispatch(setSourceDestinationBins(nextInventoryMovement));
      dispatch(setTaskState("MovingProduct"));
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  }, [
    autostoreGridId,
    confirmInventoryMovement,
    destinationBin,
    dispatch,
    errorToast,
    selectedWorkstation,
    sourceBin
  ]);

  const cancelInventoryMovement = useCallback(async () => {
    if (!autostoreGridId || !sourceBin || !selectedWorkstation) return;
    try {
      // delete all flags on the source bin
      binState?.flags.forEach((flag) => {
        if (flag === "food safety") return; // don't remove food safety flag on cancel
        void deleteBinFlag({
          autostoreGridId,
          binNumber: sourceBin.autostoreBinNumber,
          reason: flag,
          workstationId: selectedWorkstation.id
        });
      });

      // remove bin problem holds on inventory
      for (const inventory of binInventory ?? []) {
        if (
          inventory?.holds.find((hold) => hold.reasonCode === "bin problem")
        ) {
          await removeHold({
            inventoryId: inventory.inventoryId,
            workstationId: selectedWorkstation.id,
            reasonCode: "bin problem",
            autostoreGridId,
            autostorePortId: portId
          });
        }
      }

      const nextBins = await skipInventoryMovement({
        autostoreGridId,
        workstationId: selectedWorkstation.id
      }).unwrap();
      dispatch(
        setSourceDestinationBins({
          sourceAndDestinationBin: nextBins
        })
      );
      dispatch(setTaskState("MovingProduct"));
    } catch (error) {
      const errorString = getMessageFromRtkError(error);
      // if user skips the last movement, set sourceAndDestinationBin to null so that we restart all ports for cleaning
      if (errorString.toLowerCase().includes("there is nothing left to pick")) {
        dispatch(
          setSourceDestinationBins({
            sourceAndDestinationBin: null
          })
        );
        dispatch(setTaskState("MovingProduct"));
      }
      errorToast(errorString);
    }
  }, [
    autostoreGridId,
    binInventory,
    binState?.flags,
    deleteBinFlag,
    dispatch,
    errorToast,
    portId,
    removeHold,
    selectedWorkstation,
    skipInventoryMovement,
    sourceBin
  ]);

  // set isDestinationBinReady and isSourceBinReady in store so that the source/destination ports can access some info from one another
  // these cannot be passed as a prop, because the respective buttons only render for source or destination bins
  useEffect(() => {
    if (isDestinationBin && isDestinationBinReady !== undefined) {
      dispatch(setIsDestinationBinReady(isDestinationBinReady));
    }
    if (isSourceBin && isSourceBinReady !== undefined) {
      dispatch(setIsSourceBinReady(isSourceBinReady));
    }
  }, [
    isDestinationBinReady,
    isSourceBinReady,
    dispatch,
    isDestinationBin,
    isSourceBin
  ]);

  // request next bin for cleaning port
  useEffect(() => {
    if (isCleaningBin && portStatus.mode === "OPEN")
      void makeNextBinRequest(portId);
  }, [
    isCleaningBin,
    portId,
    makeNextBinRequest,
    portStatus.mode,
    binCleaningPorts
  ]);

  // refetch port/bin info when source/destination bin changes
  useEffect(() => {
    if (sourceBin?.autostoreBinNumber || destinationBin?.autostoreBinNumber) {
      refetch().catch((error) => {
        errorToast(getMessageFromRtkError(error));
      });
    }
  }, [
    sourceBin?.autostoreBinNumber,
    destinationBin?.autostoreBinNumber,
    binInventory,
    refetch,
    errorToast
  ]);

  // highlight first bin to arrive at workstation
  useEffect(() => {
    if (!movementTaskState && isReady) {
      if (isCleaningBin) dispatch(setTaskState("CleaningBin"));
      else if (isSourceBin) dispatch(setTaskState("MovingProduct"));
    }
  }, [movementTaskState, dispatch, isCleaningBin, isSourceBin, isReady]);

  // skip bin if there are no flags on it, or it is a source bin with no inventory
  useEffect(() => {
    if (
      !isReady ||
      !autostoreGridId ||
      !thisBinNumber ||
      confirmingMovement ||
      binInventoryIsFetching
    )
      return;

    const hasBinInventoryFlag = binInventory?.some(
      (item) => item?.bin?.autostoreBin?.isFlagged === true
    );

    const zeroFlags =
      (isSourceBin || isCleaningBin) &&
      binState?.flags.length === 0 &&
      !hasBinInventoryFlag;

    const noInventory = isSourceBin && binInventory?.length === 0;

    const handleNoFlag = async () => {
      if (isSourceBin) {
        await cancelInventoryMovement();
      } else if (isCleaningBin) {
        await closeBin({
          autostoreGridId,
          portId,
          binNumber: thisBinNumber,
          taskId: selectedTask
        });
        await makeNextBinRequest(portId);
      }
      Sentry.captureMessage(
        `bin ${thisBinNumber} was automatically skipped because it either had no flags or no inventory`,
        "warning"
      );
      warningToast(t("either no flags or no inventory found. skipping bin"));
    };
    if (zeroFlags || noInventory) {
      handleNoFlag().catch((err) => {
        errorToast(getMessageFromRtkError(err));
      });
    }
  }, [
    binState,
    isDestinationBin,
    isSourceBin,
    isCleaningBin,
    cancelInventoryMovement,
    getNextInventoryMovement,
    t,
    errorToast,
    warningToast,
    binInventory,
    autostoreGridId,
    thisBinNumber,
    portId,
    selectedTask,
    makeNextBinRequest,
    closeBin,
    isReady,
    confirmingMovement,
    binInventoryIsFetching
  ]);

  const isSelectedBin =
    (isSelectedCleaningBin && cleaningBin) ||
    (isSourceBin && (movingProduct || confirmingMovement));

  const compartmentOccupancy = getBinCompartmentOccupancy(binState);

  return (
    <Stack direction="row" flex={1} position="relative">
      <Stack width={"100%"}>
        {!error && (
          <Box
            sx={{
              position: "relative",
              margin: isShortSideBin ? "0 auto" : "unset"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                padding: 1,
                zIndex: 2,
                borderRadius: "0.5em",
                border: `0.625em solid`,
                borderColor: isSelectedBin ? "primary.main" : "transparent",
                animation:
                  isDestinationBin && confirmingMovement
                    ? "slide-in 750ms"
                    : "none",
                "@keyframes slide-in": {
                  from: {
                    transform: "translateX(-100%)"
                  },
                  to: {
                    transform: "translateX(0%)"
                  }
                }
              }}
              aria-selected={isSelectedBin}
              role="button"
              aria-label={`Bin ${thisBinNumber} button`}
              onClick={() => {
                if (confirmingMovement) return; // dont allow user to switch bins while confirming movement
                if (isCleaningBin) {
                  dispatch(setTaskState("CleaningBin"));
                  dispatch(setSelectedPort(portId));
                  mixpanelTrack({
                    type: "Button Click",
                    label: "Select Cleaning Bin",
                    trackedPageName,
                    eventProperties: {
                      "Bin Number": thisBinNumber,
                      "Task State": "Cleaning Bin"
                    }
                  });
                } else if (isSourceBin) {
                  dispatch(setTaskState("MovingProduct"));
                  mixpanelTrack({
                    type: "Button Click",
                    label: "Select Source Bin",
                    trackedPageName,
                    eventProperties: {
                      "Bin Number": thisBinNumber,
                      "Task State": "Moving Product"
                    }
                  });
                }
              }}
            />
            <Paper
              role="button"
              sx={{
                backgroundColor:
                  (isSourceBin && movingProduct) ||
                  (isDestinationBin && confirmingMovement)
                    ? "background.lightBlue"
                    : "background.gray",
                p: 2,
                border: "none",
                minWidth: "240px"
              }}
            >
              <AutostoreBinWithAnimation
                portId={portId}
                binId={thisBinNumber}
                pickQuantity={""}
                pickCompartment={null}
                numberOfRows={
                  binState?.binConfiguration?.horizontalCompartmentCount ?? 1
                }
                numberOfColumns={
                  binState?.binConfiguration?.verticalCompartmentCount ?? 1
                }
                compartmentNumberWithQuantities={
                  (isSourceBin && movingProduct) ||
                  (isDestinationBin && confirmingMovement)
                    ? sourceCompartmentNumbersWithQty
                    : undefined
                }
                activeTaskGroupCompartments={
                  isSourceBin
                    ? sourceCompartmentNumbersWithQty?.map((c, i) =>
                        c.autostoreCompartmentNumber
                          ? c.autostoreCompartmentNumber - 1
                          : i
                      )
                    : []
                }
                compartmentOccupancy={
                  isDestinationBin ? compartmentOccupancy : undefined
                }
                isShortSideBin={isShortSideBin}
              />
            </Paper>
          </Box>
        )}

        {/* Sets of buttons depnding on type of bin (cleaning, source, destination) */}
        {isSourceBin && (
          <Stack direction="row" justifyContent="center" spacing={2} my={5}>
            {confirmingMovement ? (
              <UnflagBinButton
                binState={binState}
                makeNextBinRequest={getNextInventoryMovement}
                portId={portId}
                disabled={
                  !isReady ||
                  !confirmingMovement ||
                  confirmInventoryMovementIsLoading
                }
              />
            ) : (
              <>
                <Button
                  variant="subtle"
                  size="large"
                  startIcon={
                    <CloseCircle20Px
                      style={{
                        opacity:
                          !isReady || !isDestinationBinReadyState ? 0.25 : 1
                      }}
                    />
                  }
                  disabled={!isReady || !isDestinationBinReadyState}
                  onClick={async () => {
                    await cancelInventoryMovement();
                    mixpanelTrack({
                      type: "Button Click",
                      label: "Cancel Inventory Movement",
                      trackedPageName
                    });
                  }}
                >
                  {t("cancel movement")}
                </Button>
                {binInventory && (
                  <MoveInventoryButton
                    sourceBinInventory={binInventory}
                    disabled={moveButtonDisabled}
                    portId={portId}
                    refetchBinInventory={async () => {
                      await refetchBinInventory();
                      await refetch();
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        )}
        {isDestinationBin &&
          !confirmingMovement && ( // dont show buttons if confirming movement
            <Stack direction="row" justifyContent="center" spacing={2} my={5}>
              <BinNotEmptyButton
                state={activityState}
                binId={thisBinNumber}
                numberOfRows={
                  binState?.binConfiguration?.horizontalCompartmentCount ?? 1
                }
                numberOfColumns={
                  binState?.binConfiguration?.verticalCompartmentCount ?? 1
                }
                portId={portId}
                binState={binState}
              />
              <FlagBinButton
                portId={portId}
                disabled={!isReady || !isSourceBinReadyState}
              />
            </Stack>
          )}
        {!!thisBinNumber && isCleaningBin && !isLoading && (
          <Stack direction="row" justifyContent="center" spacing={2} my={5}>
            <UnflagBinButton
              binState={binState}
              makeNextBinRequest={async (portId) => {
                if (thisBinNumber && autostoreGridId)
                  await closeBin({
                    autostoreGridId,
                    portId,
                    binNumber: thisBinNumber,
                    taskId: selectedTask
                  });
                await makeNextBinRequest(portId);
              }}
              portId={portId}
              disabled={!isReady || !isSelectedCleaningBin}
            />
          </Stack>
        )}
        {isLoading && (
          <Stack direction="row" justifyContent="center" spacing={2} my={5}>
            <Skeleton height={"40px"} width={"150px"} variant="rounded" />
            <Skeleton height={"40px"} width={"150px"} variant="rounded" />
          </Stack>
        )}
      </Stack>
      {isDestinationBin && movingProduct && (
        <Box position="absolute" top="calc(50% - 96px)" left="-85px">
          <PulsingArrows />
        </Box>
      )}
    </Stack>
  );
};
